import { useMutation, useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";
import type {
  ApiNodePreview,
  ApiNodePreviewRequest,
  AutomationExport,
  EffectStatus,
  EffectsResponse,
  Enrolment,
  EnrolmentJourney,
  EnrolmentsResponse,
  FlowGraph,
  ImportWorkflowResult,
  RunDetail,
  RunsResponse,
  SaveGraphResult,
  SimulatedOutcome,
  StepPreview,
  StepPreviewRequest,
  WebhookSample,
  Workflow,
  WorkflowLive,
  WorkflowTestReport,
  WorkflowsResponse,
} from "@/types/workflow";
import { downloadAutomationFile } from "@/lib/automationFile";

export const workflowsKey: QueryKey = ["agent", "workflows"];
export const runsKey: QueryKey = ["agent", "runs"];
export const enrolmentsKey: QueryKey = ["agent", "enrolments"];
/** The tenant's custom-field definitions, and every `{field}` a text box may name. Exported so a
 *  write that makes or changes a field anywhere refreshes the canvas's copies (invalidateFieldLists). */
export const agentAttributesKey: QueryKey = ["agent", "attributes"];
export const variablesKey: QueryKey = ["agent", "variables"];

export const workflowKey = (id: number): QueryKey => ["agent", "workflow", id];
export const runKey = (id: number): QueryKey => ["agent", "run", id];

/** One registered lookup. The stored credential is never sent to the browser. */
export interface AgentTool {
  id: number;
  name: string;
  description: string;
  method: string;
  enabled: boolean;
  /** Whether calling this CHANGES something. The API has always sent it; this type
   *  discarded it, so the canvas rendered a mutating lookup as a tickable capability
   *  that menu_tools then stripped before the menu was ever built -- a step
   *  advertising control it does not have, which is the exact failure the agent-node
   *  wiring exists to end. */
  mutates?: boolean;
}

export const webhookKey = (id: number): QueryKey => ["agent", "workflow", id, "webhook"];

/** One test-mode message: the published automation runs, and every provider is simulated. */
export interface WorkflowTestTurn {
  session?: string;
  text: string;
  option_id?: string;
  outcomes?: Record<string, SimulatedOutcome>;
  /** A simulated Click-to-WhatsApp ad click. Sent with the first message only, as Meta does. */
  ad_click?: { ad_id: string };
  /** "They went quiet": the conversation's pending follow-up (an AI step's nudge) fires now. */
  quiet?: boolean;
}

/** Sends one message through the published automation in test mode. Nothing reaches a customer. */
export function useWorkflowTestMutation(workflowId: number) {
  return useMutation({
    mutationFn: (turn: WorkflowTestTurn) =>
      apiFetch<WorkflowTestReport>(`/api/agent/workflows/${workflowId}/test`, {
        method: "POST",
        body: turn,
      }),
  });
}

/** Ends a test conversation and removes everything it wrote. */
export function useResetWorkflowTestMutation(workflowId: number) {
  return useMutation({
    mutationFn: (session: string) =>
      apiFetch<{ removed: number }>(`/api/agent/workflows/${workflowId}/test/${session}`, {
        method: "DELETE",
      }),
  });
}

/** One custom attribute an operator declared. The field picker offers these alongside the
 *  system fields, so a rule can name something only this tenant has. */
export interface AgentAttribute {
  key: string;
  label: string;
  value_type: string;
  options: string[];
  enabled: boolean;
  /** "ai" when "Who fills this in" is "Your team or the AI agent"; "custom" for the team alone. */
  kind: string;
  /** False when a person may set it but the agent may not. */
  ai_writable: boolean;
  /** "contact" or "opportunity". */
  object_type: string;
}

/** How long a list of fields is trusted before a step that reads it asks again. Short on purpose:
 *  an invalidation reaches only the tab that made the field, and the Custom fields screen is often
 *  open in ANOTHER tab beside a canvas mid-edit -- which, at the app's five-minute default, is how
 *  a new field took minutes to appear in a Condition. */
export const FIELD_LISTS_STALE_MS = 15_000;

/**
 * Re-read every list of custom fields a page may hold: the definitions, the `{field}` catalog and
 * the Custom fields screen's own lists. For any write that may have made or changed a field -- the
 * Custom fields screen's, a starter that plants fields, and a canvas save, which makes the field a
 * Script step keeps a value in. Exact keys, never the ["agent"] prefix: that would also re-read
 * the graph the operator is editing (useWorkflowQuery).
 */
export function invalidateFieldLists(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: agentAttributesKey });
  void queryClient.invalidateQueries({ queryKey: variablesKey });
  void queryClient.invalidateQueries({ queryKey: ["custom-fields"] });
  // The contact importer maps columns onto contact fields, a field just made included.
  void queryClient.invalidateQueries({ queryKey: ["contacts-import", "fields"] });
}

export function useAgentAttributesQuery() {
  return useQuery({
    queryKey: agentAttributesKey,
    queryFn: () => apiFetch<{ items: AgentAttribute[] }>("/api/agent/attributes"),
    // The picker is usable without them; a tenant who has declared none must not see an
    // error where a section heading would be.
    retry: false,
    staleTime: FIELD_LISTS_STALE_MS,
  });
}

/** One placeholder an operator may put in text, e.g. `contact.first_name`. `token` is written
 *  WITHOUT braces; the picker wraps it. `custom` marks a field this workspace defined. */
export interface VariableOption {
  token: string;
  label: string;
  type: string;
  custom: boolean;
}

/** The variables of one object -- the contact, their open opportunity, or what WhatsApp
 *  itself supplied (the ad click, the latest form response). */
export interface VariableGroup {
  id: "contact" | "opportunity" | "user_whatsapp";
  label: string;
  variables: VariableOption[];
}

/** Every `{object.field}` placeholder the renderer can fill, grouped by object. ONE list, read by
 *  every box that takes text -- a Message step, a Form's text, a template variable, a broadcast --
 *  so no box offers a name another would print verbatim. The server builds it from the same
 *  catalog the Custom fields screen lists, which is what keeps the two in step. */
export function useVariablesQuery() {
  return useQuery({
    queryKey: variablesKey,
    queryFn: () => apiFetch<{ groups: VariableGroup[] }>("/api/agent/variables"),
    // Typing a placeholder by hand works without the list, so a failure here must never
    // stand where the text box is.
    retry: false,
    staleTime: FIELD_LISTS_STALE_MS,
  });
}

export interface WebhookStatus {
  webhook_path: string | null;
  samples: WebhookSample[];
}

/**
 * The inbound-webhook URL and the bodies it has received.
 *
 * NOT polled. The operator fires a test request from another system and then presses
 * "check for new requests" — a background poll would spend a request every few seconds
 * for the ninety-nine percent of the time nobody is looking at this panel. `refetch` is
 * what the button calls.
 */
export function useWebhookQuery(id: number | null, enabled: boolean) {
  return useQuery({
    queryKey: webhookKey(id ?? 0),
    queryFn: () => apiFetch<WebhookStatus>(`/api/agent/workflows/${id}/webhook`),
    enabled: enabled && id !== null,
    retry: false,
  });
}

/** One WhatsApp Form the tenant built in Meta. `sendable` is the only field a picker should
 *  gate on: `form_send` sends `mode: "published"`, so anything else fails at send time. */
export interface MetaForm {
  id: string;
  name: string;
  status: string;
  sendable: boolean;
  reason: string;
  categories: string[];
  validation_errors: unknown[];
  waba_id: string;
}

/** One option a Form input lists in its own JSON. `id` is what the customer's answer ARRIVES as;
 *  `title` is what they read. */
export interface FormOption {
  id: string;
  title: string;
}

/** One component of a Form screen, flattened to what a phone needs to draw it. A property the
 *  endpoint fills is named by its key -- `data_key` for a text or an option list, `label_key`
 *  for a label -- so its value comes from the preview and its source is a binding. */
export interface FormLayoutItem {
  type: string;
  text?: string;
  data_key?: string;
  name?: string;
  label?: string;
  label_key?: string;
  required?: boolean;
  options?: FormOption[];
}

/** One input component in a Form, i.e. one key a submission comes back under. */
export interface MetaFormField {
  name: string;
  component: string;
  suggested_type: string;
  required: boolean;
  screen: string;
  label: string;
  /** The options the input lists itself; empty when a sheet or the endpoint fills them at run
   *  time. Optional so an older backend still types. */
  options?: FormOption[];
}

export interface MetaFormDetail {
  id: string;
  readable: boolean;
  version: string;
  has_endpoint: boolean;
  routing_model?: Record<string, string[]>;
  entry_screen?: string;
  screens: {
    id: string;
    title: string;
    terminal: boolean;
    data_keys: string[];
    /** What each declared key holds -- "string", "array", ... -- which decides what may fill
     *  it. Optional so an older backend still types. */
    data_types?: Record<string, string>;
    /** What the screen SHOWS, in order -- drawn by the Form Setup Studio's phone. Optional so
     *  an older backend still types. */
    layout?: FormLayoutItem[];
  }[];
  fields: MetaFormField[];
  /** Meta's own verdict on whether this Form can be sent, per entity. The ONLY thing that
   *  explains a Form which publishes, health-checks green and still will not open on a
   *  handset. Optional: unreadable health must not stop the field names being shown. */
  health?: {
    health_status?: {
      can_send_message?: string;
      entities?: {
        entity_type?: string;
        can_send_message?: string;
        errors?: { error_description?: string }[];
      }[];
    };
  };
  /** When Meta's health blamed the public key: what the key check actually found, in words the
   *  operator can act on -- or null when the key Meta holds is valid and is ours, in which case
   *  Meta's key error has been left out of `health`. */
  key_problem?: string | null;
  /** Where this Form's screens get their values, as the operator configured it. */
  bindings?: FormBindings;
  /** Each bound sheet tab's column names and row count -- never the rows themselves.
   *  `truncated`: the tab has more rows than are read. */
  sources?: Record<string, { headers: string[]; row_count: number; truncated?: boolean }>;
  sources_synced_at?: string | null;
  /** Why the last sheet refresh failed, e.g. "not_connected". Null after a success. */
  sources_error?: string | null;
  binding_warnings?: string[];
}

/** One screen key's value source. Exactly two kinds -- see form_bindings.py for why. */
export type FormBinding =
  | { kind: "text"; text: string }
  | {
      kind: "list";
      sheet: { spreadsheet_id: string; tab: string };
      columns: { id: string; title: string; description?: string; enabled?: string };
      where?: { column: string; equals: string };
    }
  | { kind: "" };

/** {screen: {key: binding}} -- the shape `PUT /forms/{id}/bindings` stores. */
export type FormBindings = Record<string, Record<string, FormBinding>>;

/** A sheet tab's column names and first rows, read BEFORE it is bound, so the editor offers the
 *  real column names rather than asking the operator to type them. `enabled` so it runs only
 *  once a spreadsheet id and tab have both been entered. */
export function useFormSheetPreviewQuery(
  flowId: string,
  spreadsheetId: string,
  tab: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["agent", "forms", flowId, "sheet", spreadsheetId, tab] as QueryKey,
    queryFn: () =>
      apiFetch<{ headers: string[]; row_count: number; truncated: boolean; sample: string[][] }>(
        `/api/agent/forms/${flowId}/sheet?spreadsheet_id=${encodeURIComponent(spreadsheetId)}` +
          `&tab=${encodeURIComponent(tab)}`,
      ),
    enabled: enabled && Boolean(flowId && spreadsheetId && tab),
    retry: false,
  });
}

/** What `PUT /forms/{id}/bindings` answers: the bindings as STORED, and the sheet rows the save
 *  just fetched. */
export type FormBindingsView = Pick<
  MetaFormDetail,
  "bindings" | "sources" | "sources_synced_at" | "sources_error" | "binding_warnings"
>;

/** Saves a Form's bindings, so the panel shows what was stored -- not what was typed.
 *
 *  THE ANSWER IS WRITTEN STRAIGHT INTO THE FORM'S CACHE, not refetched. A refetch re-reads the
 *  Flow from Meta, which takes seconds, for bindings the answer already carries. */
export function useSaveFormBindingsMutation(flowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bindings: FormBindings) =>
      apiFetch<FormBindingsView>(`/api/agent/forms/${flowId}/bindings`, {
        method: "PUT",
        body: { bindings },
      }),
    onSuccess: (view) => {
      qc.setQueryData<MetaFormDetail>(["agent", "forms", flowId], (old) =>
        old ? { ...old, ...view } : old,
      );
    },
  });
}

/** What one screen shows for a set of sample answers -- exactly what a customer gets.
 *  `served_by` is the path that answered: "bindings" or "contract" when Screen data drives the
 *  Form, "handler" when a hand-written handler still does. */
export interface FormScreenPreviewResult {
  screen: string;
  data: Record<string, unknown>;
  served_by: string;
}

/** One preview request. `contact` is a WhatsApp number (wa_id): when present the server fills
 *  `{contact.*}`, `{opportunity.*}` and `{user_whatsapp.ctwa.*}` from that person's real record.
 *  Absent -- never `undefined` -- when previewing as nobody in particular. */
export interface FormScreenPreviewRequest {
  screen: string;
  answers: Record<string, string>;
  action?: "next";
  contact?: string;
}

/** Previews one screen of a Form with sample answers. The server runs the endpoint's own routing
 *  over the SAVED bindings and cached sheet rows, so the preview cannot disagree with the phone;
 *  it calls neither Google nor Meta. */
export function usePreviewFormScreenMutation(flowId: string) {
  return useMutation({
    mutationFn: (request: FormScreenPreviewRequest) =>
      apiFetch<FormScreenPreviewResult>(`/api/agent/forms/${flowId}/preview`, {
        method: "POST",
        body: request,
      }),
  });
}

/** The tenant's Forms, read from Meta while the inspector is open and NEVER cached onto a
 *  node: a form's status is exactly the thing that goes stale. `enabled` so opening any
 *  other step costs nothing. */
export function useFormsQuery(enabled: boolean) {
  return useQuery({
    queryKey: ["agent", "forms"] as QueryKey,
    queryFn: () => apiFetch<{ items: MetaForm[]; warnings: string[] }>("/api/agent/forms"),
    enabled,
  });
}

/** One Form's screens and field names, parsed from its Flow JSON. This is what lets an
 *  operator name their collection fields to match, instead of guessing and finding out from
 *  a customer whose answer landed nowhere. */
export function useFormQuery(flowId: string) {
  return useQuery({
    queryKey: ["agent", "forms", flowId] as QueryKey,
    queryFn: () => apiFetch<MetaFormDetail>(`/api/agent/forms/${flowId}`),
    enabled: Boolean(flowId),
  });
}

/** This workspace's WhatsApp Forms endpoint: the URI to paste into Meta and the PUBLIC key
 *  to upload. The private half has no read path anywhere -- the API cannot return it. */
export interface FlowEndpointSetup {
  ready: boolean;
  endpoint_path: string;
  public_key: string;
  /** Flow ids this deployment has screen logic for. A Flow not in here can be pointed at the
   *  endpoint and will render blank screens until its handler exists. */
  served_flows: string[];
  /** How long a replaced key keeps working. Optional so an older backend still types. */
  overlap_hours?: number;
}

export function useFlowEndpointQuery() {
  return useQuery({
    queryKey: ["flows", "endpoint"] as QueryKey,
    queryFn: () => apiFetch<FlowEndpointSetup>("/api/meta/whatsapp/flows-setup/me"),
    retry: false,
  });
}

/** Mints or ROTATES the key. There is no overlap window: the previous key stops working the
 *  moment this resolves, so the caller must confirm before firing it at a live Form. */
export function useMintFlowKeyMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ endpoint_path: string; public_key: string; rotated: boolean }>(
        "/api/meta/whatsapp/flows-setup/key",
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["flows", "endpoint"] });
    },
  });
}

/** Uploads the PUBLIC key to Meta against this tenant's phone number. Meta's Builder has no
 *  field for this -- its "Sign public key" step is a link to the API -- so this is the button
 *  that replaces a curl carrying the operator's own access token. */
export function useRegisterFlowKeyMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ phone_number_id: string; signature_status: string; fields: string[] }>(
        "/api/meta/whatsapp/flows-setup/register",
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["flows", "endpoint"] });
    },
  });
}

/**
 * Sends a Data Lookup step's own draft config for real and returns the whole exchange.
 *
 * `retry: false` on purpose: a POST rehearsal may create something on the tenant's side, so a
 * transport retry would be a second one. The server answers HTTP 200 for a refusal or a failed
 * call (`ok: false`, `reason`); only a transport failure or a 5xx from THIS API rejects here.
 */
export function usePreviewApiNodeMutation() {
  return useMutation({
    mutationFn: (request: ApiNodePreviewRequest) =>
      apiFetch<ApiNodePreview>("/api/agent/api-node/preview", {
        method: "POST",
        body: request,
      }),
    retry: false,
  });
}

export function useAgentToolsQuery(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["agent", "tools"] as QueryKey,
    queryFn: () => apiFetch<{ items: AgentTool[] }>("/api/agent/tools"),
    ...(options.enabled === undefined ? {} : { enabled: options.enabled }),
  });
}

/** Whether this tenant has a usable Google grant. Never returns a credential. */
export interface GoogleStatus {
  connected: boolean;
  configured: boolean;
  email: string | null;
  scopes: string[];
  needs_reconnect: boolean;
  reason: string | null;
}

/** A spreadsheet's tabs. Doubles as a reachability check while configuring. */
export function useSheetTabsQuery(spreadsheetId: string) {
  return useQuery({
    queryKey: ["google", "tabs", spreadsheetId] as QueryKey,
    queryFn: () =>
      apiFetch<{ title: string; tabs: string[] }>(
        `/api/google/sheets/tabs?spreadsheet_id=${encodeURIComponent(spreadsheetId)}`,
      ),
    enabled: spreadsheetId.trim().length > 0,
    retry: false,
  });
}

/** Row 1 of a tab, so columns map by name instead of by position. */
export function useSheetHeadersQuery(spreadsheetId: string, tab: string) {
  return useQuery({
    queryKey: ["google", "headers", spreadsheetId, tab] as QueryKey,
    queryFn: () =>
      apiFetch<{ headers: string[] }>(
        `/api/google/sheets/headers?spreadsheet_id=${encodeURIComponent(spreadsheetId)}` +
          `&tab=${encodeURIComponent(tab)}`,
      ),
    enabled: spreadsheetId.trim().length > 0 && tab.trim().length > 0,
    retry: false,
  });
}

export function useGoogleStatusQuery() {
  return useQuery({
    queryKey: ["google", "status"] as QueryKey,
    queryFn: () => apiFetch<GoogleStatus>("/api/google/status"),
  });
}

/**
 * Sends a Sheets or Calendar step's own draft config for real and returns the whole exchange.
 * `retry: false` on purpose, same as `usePreviewApiNodeMutation`: a Sheets append/update or
 * Calendar create_event never sends for real (the server always answers `dry_run` for those), but
 * a Sheets `lookup` or Calendar `find_slot` really reads, and a transport retry would be a second
 * read against a live API. The server answers HTTP 200 for every ordinary failure.
 */
export function usePreviewStepMutation() {
  return useMutation({
    mutationFn: (request: StepPreviewRequest) =>
      apiFetch<StepPreview>("/api/agent/workflows/step-preview", {
        method: "POST",
        body: request,
      }),
    retry: false,
  });
}

export function useWorkflowsQuery() {
  return useQuery({
    queryKey: workflowsKey,
    queryFn: () => apiFetch<WorkflowsResponse>("/api/agent/workflows"),
  });
}

export function useWorkflowQuery(id: number | null) {
  return useQuery({
    queryKey: workflowKey(id ?? 0),
    queryFn: () => apiFetch<Workflow>(`/api/agent/workflows/${id}`),
    enabled: id != null,
    // The canvas holds the graph in local state while it is open. A refetch under the
    // operator would replace what they are dragging, so this is fetched once and then left
    // alone until they navigate away.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    // Off, so setQueryData stores the graph a save hands it BY REFERENCE. With React Query's
    // default structural sharing the cache held a deep copy, the canvas's identity check
    // (seededFrom !== data.graph) failed after every save, and each save re-seeded the canvas.
    structuralSharing: false,
  });
}

export function useCreateWorkflowMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      apiFetch<Workflow>("/api/agent/workflows", { method: "POST", body: { name } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: workflowsKey });
    },
  });
}

/**
 * Plant a named starter automation.
 *
 * The server publishes what it plants, so the row comes back LIVE and the list has to be
 * refetched rather than patched — the enrolled counts and the live/draft tallies on that
 * screen are all read off it.
 *
 * A 409 means this tenant already has an automation answering every incoming message. The
 * caller shows that sentence rather than swallowing it: it names the automation, and
 * "nothing happened" is the one response that leaves the operator pressing again.
 */
export function useCreateFromTemplateMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (template: string) =>
      apiFetch<Workflow>("/api/agent/workflows/from-template", {
        method: "POST",
        body: { template },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: workflowsKey });
      // A starter may plant the fields its own graph needs, server-side (plant_template).
      invalidateFieldLists(queryClient);
    },
  });
}

/**
 * Download one automation as a file: `clean_graph`'s own shape, with every header value
 * blanked by the server -- never `serialise()`, which carries the webhook secret and
 * captured customer data. Takes the DRAFT graph, whatever is live or not.
 *
 * A mutation rather than a query: it triggers a browser download as its side effect, and a
 * query a component re-renders is expected to re-run for free -- which would download the
 * file again on every render.
 */
export function useExportWorkflowMutation() {
  return useMutation({
    mutationFn: async (id: number) => {
      const doc = await apiFetch<AutomationExport>(`/api/agent/workflows/${id}/export`);
      downloadAutomationFile(doc);
      return doc;
    },
  });
}

/**
 * Import an automation from a file `readAutomationFile` already parsed. Lands as a new DRAFT,
 * possibly in a different workspace than the one that exported it, so the list is
 * invalidated rather than patched -- there is no existing cache entry to update.
 */
export function useImportWorkflowMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (doc: unknown) =>
      apiFetch<ImportWorkflowResult>("/api/agent/workflows/import", {
        method: "POST",
        body: doc,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: workflowsKey });
    },
  });
}

/**
 * Save the draft graph (the canvas's Save; nothing saves by itself since 2026-10-01).
 *
 * Deliberately does NOT invalidate the workflow query: the canvas is the source of truth
 * while it is open, and refetching would hand back a copy that is at best identical and at
 * worst a frame behind what the operator is dragging.
 *
 * It MUST, however, write the acknowledged graph into that cache. Not doing so lost work
 * outright: useWorkflowQuery is staleTime:Infinity, so the entry stayed frozen at the
 * one-trigger graph the automation was created with. Leave the canvas and come back and
 * React Query served that stale entry instantly, the canvas re-seeded from it, and the next
 * autosave posted one node over the four the server actually held. The operator watched
 * their automation revert to new and had no way to know their steps had just been deleted.
 */
export function useSaveGraphMutation(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ graph, name }: { graph: FlowGraph; name?: string }) =>
      apiFetch<SaveGraphResult>(`/api/agent/workflows/${id}/graph`, {
        method: "PUT",
        body: name ? { graph, name } : { graph },
      }),
    onSuccess: (_result, variables) => {
      // The same object the canvas passed in, stored by reference on purpose: the canvas
      // records it as already-seeded, so patching the cache cannot bounce back and re-seed
      // the graph out from under an edit in progress.
      queryClient.setQueryData(workflowKey(id), (previous?: Workflow) =>
        previous
          ? {
              ...previous,
              graph: variables.graph,
              ...(variables.name ? { name: variables.name } : {}),
            }
          : previous,
      );
      // The list shows a step count off the same draft graph, and it is a different query.
      void queryClient.invalidateQueries({ queryKey: workflowsKey });
      // The save may have made the custom field a Script step keeps a value in
      // (ensure_kept_fields), so the field lists are re-read -- never the graph itself.
      invalidateFieldLists(queryClient);
    },
    retry: false,
  });
}

export interface WorkflowVersion {
  id: number;
  version: number;
  steps: number;
  is_live: boolean;
  published_at: string | null;
}

export interface VersionsResponse {
  draft_steps: number;
  live_version_id: number | null;
  items: WorkflowVersion[];
}

export const versionsKey = (id: number): QueryKey => ["agent", "workflow", id, "versions"];

/**
 * The published history of one automation.
 *
 * Worth its own query rather than folding into the workflow payload: the canvas reads the
 * workflow on every open, and this is only needed when somebody is looking for a graph they
 * lost.
 */
export function useWorkflowVersionsQuery(id: number | null) {
  return useQuery({
    queryKey: versionsKey(id ?? 0),
    queryFn: () => apiFetch<VersionsResponse>(`/api/agent/workflows/${id}/versions`),
    enabled: id != null,
  });
}

/**
 * Copy a published version back over the draft.
 *
 * Invalidates the workflow rather than patching it, which is the opposite of what saving
 * does — and correct here for the same underlying reason. Restoring exists precisely to
 * replace what the canvas is holding, so the refetch is the point, not a hazard.
 */
export function useRestoreVersionMutation(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (version: number) =>
      apiFetch<{ restored: number; graph: FlowGraph; problems: string[] }>(
        `/api/agent/workflows/${id}/restore/${version}`,
        { method: "POST" },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: workflowKey(id) });
      void queryClient.invalidateQueries({ queryKey: versionsKey(id) });
      void queryClient.invalidateQueries({ queryKey: workflowsKey });
    },
    retry: false,
  });
}

export function usePublishWorkflowMutation(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ status: string; version: number }>(
        `/api/agent/workflows/${id}/publish`,
        { method: "POST" },
      ),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: workflowsKey });
      // Who waits where, and what the draft changes for them, both moved with the publish.
      void queryClient.invalidateQueries({ queryKey: liveKey(id) });
      // PATCHED IN PLACE, never invalidated. Invalidating refetches the workflow, and the
      // canvas seeds its local graph from that payload by identity -- so a publish would
      // hand back a fresh object and silently overwrite whatever the operator had dragged
      // since the last save. Only status and version changed; write those two.
      queryClient.setQueryData(workflowKey(id), (previous?: Workflow) =>
        previous ? { ...previous, status: "live" as const, version: result.version } : previous,
      );
    },
    // Publish makes a kept Script value's missing custom field BEFORE it checks anything, and
    // that field stays made when the checks then refuse -- so success or a 422, the field lists
    // are re-read. Save sends nothing for an unchanged graph, so this can be the only write.
    onSettled: () => {
      invalidateFieldLists(queryClient);
    },
    // A 422 carries the problems the operator must fix. Retrying repeats a decision that
    // will not change.
    retry: false,
  });
}

export function usePauseWorkflowMutation(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ status: string }>(`/api/agent/workflows/${id}/pause`, { method: "POST" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: workflowsKey });
      // Patched, not invalidated — same reason as publish above.
      queryClient.setQueryData(workflowKey(id), (previous?: Workflow) =>
        previous ? { ...previous, status: "draft" as const } : previous,
      );
    },
  });
}

export function useDeleteWorkflowMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<{ status: string }>(`/api/agent/workflows/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: workflowsKey });
    },
    // A 409 means contacts are still inside. That is an answer, not a transient fault.
    retry: false,
  });
}

export function useRunsQuery(
  filter: {
    contactId?: string | undefined;
    workflowId?: number | undefined;
    status?: string | null | undefined;
    q?: string | null | undefined;
    since?: string | null | undefined;
    until?: string | null | undefined;
    limit?: number | undefined;
    offset?: number | undefined;
  } = {},
) {
  const { contactId, workflowId, status, q, since, until, limit, offset } = filter;
  const params = new URLSearchParams();
  if (contactId) params.set("contact_id", contactId);
  if (workflowId != null) params.set("workflow_id", String(workflowId));
  if (status && status !== "all") params.set("status", status);
  if (q) params.set("q", q);
  if (since) params.set("since", since);
  if (until) params.set("until", until);
  if (limit != null) params.set("limit", String(limit));
  if (offset != null) params.set("offset", String(offset));
  const qs = params.toString();
  return useQuery({
    queryKey: [...runsKey, contactId ?? null, workflowId ?? null, status, q, since, until, limit, offset],
    queryFn: () => apiFetch<RunsResponse>(qs ? `/api/agent/runs?${qs}` : "/api/agent/runs"),
  });
}

export function useRunQuery(id: number | null) {
  return useQuery({
    queryKey: runKey(id ?? 0),
    queryFn: () => apiFetch<RunDetail>(`/api/agent/runs/${id}`),
    enabled: id != null,
    // A finished run never changes, so there is nothing to refetch.
    staleTime: Infinity,
  });
}

export interface ReplayResult {
  message: string;
  before: unknown;
  after: {
    intent?: string;
    action?: string;
    confidence?: number;
    agent?: string | null;
    reply?: string;
    handoff_reason?: string | null;
    knowledge_used?: number[];
  };
  replay_run_id: number | null;
}

/**
 * Re-run one step against its recorded input, with today's configuration.
 *
 * Sends nothing and advances nothing — `agent_reply` imports no send function at all, which
 * the backend suite asserts rather than trusting. Not retried: a replay costs a model call,
 * and if today's configuration raises, the test panel shows that error directly.
 */
export function useReplayStepMutation() {
  return useMutation({
    mutationFn: ({ runId, seq }: { runId: number; seq: number }) =>
      apiFetch<ReplayResult>(`/api/agent/runs/${runId}/steps/${seq}/replay`, {
        method: "POST",
      }),
    retry: false,
  });
}

export interface AgentEvent {
  id: number;
  channel: string;
  contact_id: string;
  decision: string;
  reason: string | null;
  intent: string | null;
  confidence: number | null;
  draft_text: string | null;
  created_at: string | null;
}

export const eventsKey: QueryKey = ["agent", "events"];

/**
 * Every decision the agent made, INCLUDING the ones not to answer.
 *
 * A run is only recorded once a message clears gates 1-8, so a message stopped at a gate
 * leaves no run at all -- and the execution history shows nothing, which reads as "the
 * automation never fired" with no way to find out why. This endpoint has always held the
 * answer in `reason`; it just had nothing reading it.
 */
export function useAgentEventsQuery() {
  return useQuery({
    queryKey: eventsKey,
    queryFn: () => apiFetch<{ events: AgentEvent[] }>("/api/agent/events?limit=50"),
  });
}

export function useEnrolmentsQuery(
  options?:
    | {
        workflowId?: number | undefined;
        status?: string | null | undefined;
        q?: string | null | undefined;
        since?: string | null | undefined;
        until?: string | null | undefined;
        limit?: number | undefined;
        offset?: number | undefined;
      }
    | number,
) {
  const opts = typeof options === "number" ? { workflowId: options } : (options ?? {});
  const { workflowId, status, q, since, until, limit, offset } = opts;
  const params = new URLSearchParams();
  if (workflowId != null) params.set("workflow_id", String(workflowId));
  if (status && status !== "all") params.set("status", status);
  if (q) params.set("q", q);
  if (since) params.set("since", since);
  if (until) params.set("until", until);
  if (limit != null) params.set("limit", String(limit));
  if (offset != null) params.set("offset", String(offset));
  const qs = params.toString();
  return useQuery({
    queryKey: [...enrolmentsKey, workflowId ?? null, status, q, since, until, limit, offset],
    queryFn: () =>
      apiFetch<EnrolmentsResponse>(qs ? `/api/agent/enrolments?${qs}` : "/api/agent/enrolments"),
  });
}

export function useCancelEnrolmentMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<Enrolment>(`/api/agent/enrolments/${id}/cancel`, { method: "POST" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: enrolmentsKey });
      void queryClient.invalidateQueries({ queryKey: workflowsKey });
      void queryClient.invalidateQueries({ queryKey: journeyPrefix });
    },
  });
}

const journeyPrefix: QueryKey = ["agent", "enrolment"];
export const journeyKey = (id: number): QueryKey => [...journeyPrefix, id, "journey"];

/**
 * ONE customer's whole journey through one automation: every run in order, the path through
 * the nodes, and what they wait on now -- drawn on the version they are PINNED to, which is
 * why it carries its own `graph` rather than borrowing the draft the canvas edits.
 *
 * Not polled: it is a record an operator reads. The canvas's live view is what refreshes.
 */
export function useEnrolmentJourneyQuery(id: number | null) {
  return useQuery({
    queryKey: journeyKey(id ?? 0),
    queryFn: () => apiFetch<EnrolmentJourney>(`/api/agent/enrolments/${id}/journey`),
    enabled: id != null,
    // A 404 (not this tenant's, or a test conversation) will not change on a retry.
    retry: false,
  });
}

/** How often the canvas re-asks who is waiting where while it is open. */
export const LIVE_REFRESH_MS = 15_000;

/** Under the workflow's own key, so restoring a version (which invalidates it) refreshes this. */
export const liveKey = (id: number): QueryKey => ["agent", "workflow", id, "live"];

/**
 * Who is inside this automation right now, on which node, on which version -- and what the
 * DRAFT would change for them, which is what the publish warning reads.
 *
 * Polled every LIVE_REFRESH_MS while `enabled`, and only while the page is visible (React
 * Query's default): nobody reads counts on a tab in the background.
 */
export function useWorkflowLiveQuery(id: number | null, enabled = true) {
  return useQuery({
    queryKey: liveKey(id ?? 0),
    queryFn: () => apiFetch<WorkflowLive>(`/api/agent/workflows/${id}/live`),
    enabled: enabled && id != null,
    refetchInterval: LIVE_REFRESH_MS,
    retry: false,
  });
}

export const effectsKey: QueryKey = ["agent", "effects"];

/** The effect ledger. `unknown` is the status that waits on a person. */
export function useEffectsQuery(status: EffectStatus | null = "unknown") {
  return useQuery({
    queryKey: [...effectsKey, status ?? "all"],
    queryFn: () =>
      apiFetch<EffectsResponse>(
        status ? `/api/agent/effects?status=${status}` : "/api/agent/effects",
      ),
  });
}

/** The unresolved effects of ONE conversation -- what the inbox and the contact record ask, so
 *  a chat waiting on an operator says so where they are already looking. */
export function useConversationIssuesQuery(channel: string, contactId: string) {
  return useQuery({
    queryKey: [...effectsKey, "conversation", channel, contactId],
    queryFn: () =>
      apiFetch<EffectsResponse>(
        `/api/agent/effects?status=unknown&channel=${encodeURIComponent(channel)}` +
          `&contact_id=${encodeURIComponent(contactId)}`,
      ),
    enabled: Boolean(channel && contactId),
  });
}

/** An operator's verdict on an unknown effect, with what they checked. The server records both,
 *  and carries the automation on without calling the provider again. */
export function useResolveEffectMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: number; decision: "succeeded" | "failed"; evidence: string }) =>
      apiFetch<{ status: string; purpose: string; carried: string }>(
        `/api/agent/effects/${input.id}/resolve`,
        { method: "POST", body: { decision: input.decision, evidence: input.evidence } },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: effectsKey });
      void queryClient.invalidateQueries({ queryKey: enrolmentsKey });
      void queryClient.invalidateQueries({ queryKey: runsKey });
    },
  });
}

/** Micro-dollars to something a person reads. A run costs a fraction of a cent. */
export function formatMicros(micros: number): string {
  if (!micros) return "$0";
  const dollars = micros / 1_000_000;
  return dollars < 0.01 ? `$${dollars.toFixed(4)}` : `$${dollars.toFixed(2)}`;
}

/** Milliseconds to something a person reads, without a false sense of precision. */
export function formatMs(ms: number | null): string {
  if (ms == null) return "—";
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
}
